/** team.thetabletop.in — staff front door (login + tile home), separate from Manage. */

export const TEAM_HUB_HOST = "team.thetabletop.in";
export const MANAGE_HOST = "manage.thetabletop.in";

/* Opening Ops used to need a cross-origin handoff — an HttpOnly parent-domain
 * cookie plus a claim endpoint — because Ops lived on MANAGE_HOST, localStorage
 * is per-origin, and a token in the URL would outlive the redirect in history
 * and Referer. Ops is at /ops on this origin now, so the token is simply
 * stored and that whole mechanism is gone. MANAGE_HOST stays: it still serves
 * every customer path in docs/ops/outbound-url-inventory.md. */

/** Absolute Team hub URL — the canonical staff front door. */
export function teamHubUrl(path = "/") {
  const raw = String(path || "/");
  const safe =
    raw.startsWith("/") && !raw.startsWith("//") && !raw.includes("://") ? raw : "/";
  return `https://${TEAM_HUB_HOST}${safe}`;
}

export function isTeamHubHost(hostname = typeof window !== "undefined" ? window.location.hostname : "") {
  const host = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
  return host === TEAM_HUB_HOST || host === `www.${TEAM_HUB_HOST}`;
}

/** Public app paths on the Team hub (no /ts prefix). */
export const TEAM_APP_PATHS = {
  kitchen: "/kitchen",
  driver: "/delivery",
  inventory: "/inventory",
  chat: "/reports",
  salary: "/salary",
  we: "/work-ethic",
  gallery: "/designs",
  tc: "/chat",
};

export function teamHomePath() {
  return "/";
}

/** Home path for the current host (Team hub `/` vs Manage `/ts`). */
export function staffHomePath() {
  return isTeamHubHost() ? teamHomePath() : "/ts";
}

/** Login path for the current host. */
export function staffLoginPath(nextPath = "") {
  if (isTeamHubHost()) return teamLoginPath(nextPath);
  const next = String(nextPath || "").trim();
  if (next && next.startsWith("/") && !next.startsWith("//") && !next.includes("://")) {
    return `/ts/login?next=${encodeURIComponent(next)}`;
  }
  return "/ts/login";
}

export function teamLoginPath(nextPath = "") {
  const next = String(nextPath || "").trim();
  if (next && next.startsWith("/") && !next.startsWith("//") && !next.includes("://") && next !== "/") {
    return `/?next=${encodeURIComponent(next)}`;
  }
  return "/";
}

/** Map legacy /ts/... paths to Team hub paths (or home). */
export function teamPathFromLegacyTs(pathname = "") {
  const p = String(pathname || "");
  if (p === "/ts" || p === "/ts/" || p === "/ts/login") return "/";
  const map = {
    "/ts/app/kitchen": TEAM_APP_PATHS.kitchen,
    "/ts/app/delivery": TEAM_APP_PATHS.driver,
    "/ts/app/inventory": TEAM_APP_PATHS.inventory,
    "/ts/app/chat": TEAM_APP_PATHS.chat,
    "/ts/app/salary": TEAM_APP_PATHS.salary,
    "/ts/app/we": TEAM_APP_PATHS.we,
    "/ts/app/gallery": TEAM_APP_PATHS.gallery,
    "/ts/app/tc": TEAM_APP_PATHS.tc,
  };
  return map[p] || "/";
}

export function tsAppPathsForHost() {
  if (isTeamHubHost()) {
    return {
      kitchen: TEAM_APP_PATHS.kitchen,
      driver: TEAM_APP_PATHS.driver,
      inventory: TEAM_APP_PATHS.inventory,
      chat: TEAM_APP_PATHS.chat,
      salary: TEAM_APP_PATHS.salary,
      we: TEAM_APP_PATHS.we,
      gallery: TEAM_APP_PATHS.gallery,
    };
  }
  return null;
}

/** Brand labels — Team hub vs legacy Manage /ts shell. */
export function staffBrandKicker() {
  return isTeamHubHost() ? "The Table Top" : "The Table Top System";
}

export function staffBrandTitle() {
  return isTeamHubHost() ? "Team" : "TS";
}

/** Delivery partner join path (same on Team hub and Manage). */
export function deliveryJoinPath() {
  return "/dd/join";
}
